/*
  This page is rendered inside the Zuora iFrame, as path '/payment-method/card-callback',
  with QueryParameters:
{
  success: boolean
  responseFrom: Response_From_Submit_Page
  token: string
  signature: string
  tenantId: string
  refId?: string
  errorCode?: string
  errorMessage?: string
}
*/

import { useEffect } from 'react';

import {
  OnPaymentMethodCallbackErrorHandler,
  OnPaymentMethodCallbackSuccessHandler,
  PaymentCallbackError,
  PaymentMethodCallbackPageProps,
} from 'apps-common/components/PaymentCallbackPage';
import { useQueryParams } from 'apps-common/hooks/useQueryParams';
import { AppType, BillingPeriod, Currency, FlowType, PaymentMethodType, UserAddressForm } from 'apps-common/types';
import { convertPaymentMethodTypeToTrackingType } from 'apps-common/utils/analytics';
import { logger } from 'apps-common/utils/logger';
import { getTranslationLanguage } from 'translations';
import { Loader } from 'ui';

const creditCardCallbackHandler = (
  queryParams: URLSearchParams,
  appType: AppType,
  onSuccess: OnPaymentMethodCallbackSuccessHandler,
  onError: OnPaymentMethodCallbackErrorHandler,
  setPaymentError: (error: Error) => void,
  billingPeriod: BillingPeriod,
  currency: Currency,
  email: string,
  userAddressForm: UserAddressForm,
  completeRoute: string,
  paymentMethodRoute: string,
  renewCompleteRoute?: string,
) => {
  logger.debug('User directed to Zuora card-callback page', {
    queryParams,
  });
  const paymentMethodId = queryParams.get('refId');
  const paymentGatewayName = queryParams.get('field_passthrough1');
  const flowType = FlowType[queryParams.get('field_passthrough2') as keyof typeof FlowType];
  const paymentMethodType = paymentGatewayName?.startsWith('adyen')
    ? PaymentMethodType.HSA
    : PaymentMethodType.CreditCard;
  const supportedTranslations = JSON.parse(import.meta.env.VITE_TRANSLATIONS_SUPPORTED) as string[];
  const locale = getTranslationLanguage(supportedTranslations);
  const paymentMethodCallbackTrackingData = {
    locale,
    paymentMethodType: convertPaymentMethodTypeToTrackingType(paymentMethodType),
    currency,
    billingPeriod,
    paymentGateway: paymentGatewayName ?? '',
  };

  if (
    queryParams.get('responseFrom') === 'Response_From_Submit_Page' &&
    queryParams.get('success') === 'true' &&
    paymentMethodId &&
    paymentGatewayName &&
    flowType &&
    currency
  ) {
    const successRoute = flowType === FlowType.Renew ? renewCompleteRoute! : completeRoute;
    void onSuccess(
      flowType,
      email,
      userAddressForm,
      paymentMethodId,
      successRoute,
      paymentMethodRoute,
      paymentMethodCallbackTrackingData,
    );
  } else {
    const errorMessage = queryParams.get('errorMessage') ?? '';
    const errorCode = queryParams.get('errorCode') ?? '';
    const error = new PaymentCallbackError(decodeURIComponent(errorMessage), errorCode);
    setPaymentError(error);

    onError(paymentMethodRoute, error, appType, paymentMethodCallbackTrackingData);
  }
};

const useHandleCreditCardCallback = ({
  onSuccess,
  onError,
  setPaymentError,
  billingPeriod,
  currency,
  email,
  userAddressForm,
  completeRoute,
  paymentMethodRoute,
  renewCompleteRoute,
  appType,
}: PaymentMethodCallbackPageProps) => {
  const queryParams = useQueryParams();
  useEffect(() => {
    if (!billingPeriod) {
      throw new Error('Billing period is required');
    }
    if (!currency) {
      throw new Error('Currency is required');
    }
    creditCardCallbackHandler(
      queryParams,
      appType,
      onSuccess,
      onError,
      setPaymentError,
      billingPeriod,
      currency,
      email,
      userAddressForm,
      completeRoute,
      paymentMethodRoute,
      renewCompleteRoute,
    );
  }, [
    queryParams,
    appType,
    onSuccess,
    onError,
    setPaymentError,
    billingPeriod,
    currency,
    email,
    userAddressForm,
    completeRoute,
    paymentMethodRoute,
    renewCompleteRoute,
  ]);
};

export const CreditCardCallbackPage = (props: PaymentMethodCallbackPageProps) => {
  useHandleCreditCardCallback(props);

  return <Loader />;
};
